import moment from 'moment';

import { APIClient } from './api/useAPIClient';
import { QuestionFilter } from './hooks/useSearchCandidates';
import {
  JobPostingDetail,
  RecruitmentProcessCollection,
  SankhyaCompany,
  SankhyaDepartment,
  SankhyaJobRole,
  SerialID,
  PermissionCodenames,
  VixtingCompany,
  VixtingDepartment,
  VixtingJobRole,
} from './types';
import { PermissionCollectionSerializer } from './types/api';

const calculateOpenDays = (openDay: string, finalizationDay?: string | null) => {
  const today = moment();
  const vacancyDate = moment(openDay);

  if (finalizationDay) {
    const finalizationDate = moment(finalizationDay);
    if (finalizationDate.isBefore(today, 'day')) {
      return Math.abs(finalizationDate.diff(vacancyDate, 'days')) + 1;
    }
  }

  return Math.abs(vacancyDate.diff(today, 'days'));
};

const markdownToQuillHtml = (text: string) => {
  return text.replaceAll('\n', '<br>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
};

const cleanHtmlRegex = /<[^>]*>/g;

const cleanQuillHtmlContent = (content?: string) => {
  return content?.replaceAll(cleanHtmlRegex, '').trim();
};

const currencyFormatter = (value: number | string | null | undefined) => {
  if (!value && value !== 0) return '';

  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const currencyParser = (displayValue: string | undefined) => {
  if (!displayValue) return '';
  const onlyNumbers = displayValue.replace(/\D/g, '');
  if (!onlyNumbers) return 0;
  return parseInt(onlyNumbers, 10) / 100;
};

//#region Vixting Mappings

const mapVixtingCompany = (vixtingCompanies: VixtingCompany[]) =>
  vixtingCompanies.map(
    (comp) => ({ CODEMP: comp.id, Empresa_NOMEFANTASIA: comp.name } as SankhyaCompany)
  );
const mapVixtingJobRole = (vixtingJobRoles: VixtingJobRole[]) =>
  vixtingJobRoles.map((role) => ({ CODCARGO: role.id, DESCRCARGO: role.name } as SankhyaJobRole));
const mapVixtingDepartment = (vixtingDepartments: VixtingDepartment[]) =>
  vixtingDepartments.map((dep) => ({ CODDEP: dep.id, DESCRDEP: dep.name } as SankhyaDepartment));

//#endregion

//#region Query param building

const buildCandidateQueryParam = (searchParams: URLSearchParams) => {
  const functionIdQueryParam = searchParams.get('match')
    ? Number(searchParams.get('match'))
    : undefined;

  let birthdateStart = undefined;
  let birthdateEnd = undefined;

  if (searchParams.get('age_start')) {
    birthdateEnd = moment()
      .subtract(parseInt(searchParams.get('age_start') as string, 10), 'years')
      .format('YYYY-MM-DD');
  }

  if (searchParams.get('age_end')) {
    birthdateStart = moment()
      .subtract(parseInt(searchParams.get('age_end') as string, 10), 'years')
      .format('YYYY-MM-DD');
  }

  return {
    search: searchParams.get('search') || undefined,
    address_country__iin: searchParams.getAll('addressContryIn').join(',') || undefined,
    address_state__iin: searchParams.getAll('addressStateIn').join(',') || undefined,
    address_city__iin: searchParams.getAll('addressCityIn').join(',') || undefined,
    updated_at__gte: searchParams.get('updatedAtMin') || undefined,
    updated_at__lte: searchParams.get('updatedAtMax') || undefined,
    created_at__gte: searchParams.get('createdAtMin') || undefined,
    created_at__lte: searchParams.get('createdAtMax') || undefined,
    function_id: functionIdQueryParam,
    ...(functionIdQueryParam
      ? {
          match__score__gte: searchParams.get('match_min')
            ? Number(searchParams.get('match_min'))
            : undefined,
        }
      : {}),
    ...(functionIdQueryParam
      ? {
          match__score__lte: searchParams.get('match_max')
            ? Number(searchParams.get('match_max'))
            : undefined,
        }
      : {}),
    recent_logic__gte: searchParams.get('logic_min')
      ? Number(searchParams.get('logic_min'))
      : undefined,
    recent_logic__lte: searchParams.get('logic_max')
      ? Number(searchParams.get('logic_max'))
      : undefined,
    recent_motivational__gte: searchParams.get('motivational_min')
      ? Number(searchParams.get('motivational_min'))
      : undefined,
    recent_motivational__lte: searchParams.get('motivational_max')
      ? Number(searchParams.get('motivational_max'))
      : undefined,
    recent_ocp__gte: searchParams.get('ocp_min') ? Number(searchParams.get('ocp_min')) : undefined,
    recent_ocp__lte: searchParams.get('ocp_max') ? Number(searchParams.get('ocp_max')) : undefined,
    recent_raw_potential__gte: searchParams.get('raw_potential_min')
      ? Number(searchParams.get('raw_potential_min'))
      : undefined,
    recent_raw_potential__lte: searchParams.get('raw_potential_max')
      ? Number(searchParams.get('raw_potential_max'))
      : undefined,
    recent_sjt__gte: searchParams.get('sjt_min') ? Number(searchParams.get('sjt_min')) : undefined,
    recent_sjt__lte: searchParams.get('sjt_max') ? Number(searchParams.get('sjt_max')) : undefined,
    recent_operational:
      searchParams.get('operational_logic') === 'Aprovado'
        ? 1
        : searchParams.get('operational_logic') === 'Reprovado'
        ? 0
        : undefined,
    birthdate__lte: birthdateEnd,
    birthdate__gte: birthdateStart,
    salary_expectation__lte: searchParams.get('salary_expectation_end')
      ? parseInt(searchParams.get('salary_expectation_end') as string, 10)
      : undefined,
    salary_expectation__gte: searchParams.get('salary_expectation_start')
      ? parseInt(searchParams.get('salary_expectation_start') as string, 10)
      : undefined,
    disabilities__iin: searchParams.getAll('disabilities').join(',') || undefined,
    candidate_source_option__in:
      searchParams.getAll('candidateSourceOptions').join(',') || undefined,
    tags__id__in: searchParams.getAll('tags').join(',') || undefined,
  };
};

const buildRecruitmentProcessQueryParam = async (
  searchParams: URLSearchParams,
  api: APIClient,
  jobPostingId?: JobPostingDetail['id'],
  functionId?: number | null
) => {
  const functionIdQueryParam =
    functionId || (searchParams.get('match') ? Number(searchParams.get('match')) : undefined);

  let birthdateStart = undefined;
  let birthdateEnd = undefined;

  if (searchParams.get('age_start')) {
    birthdateEnd = moment()
      .subtract(parseInt(searchParams.get('age_start') as string, 10), 'years')
      .format('YYYY-MM-DD');
  }

  if (searchParams.get('age_end')) {
    birthdateStart = moment()
      .subtract(parseInt(searchParams.get('age_end') as string, 10), 'years')
      .format('YYYY-MM-DD');
  }

  const stage = searchParams.get('stages');
  let stageId: SerialID | undefined;

  if (['SUBSCRIBED', 'SELECTED', 'APPROVED', 'HIRED'].includes(stage || '')) {
    const stages = await api.get('/stages/', {
      params: {
        job_posting_id: jobPostingId,
        type: stage as 'SUBSCRIBED' | 'SELECTED' | 'APPROVED' | 'HIRED',
      },
    });
    stageId = stages.data.results[0]?.id;
  } else if (stage && !isNaN(Number(stage))) {
    stageId = Number(stage);
  }

  const questionFilters = Array.from(searchParams.entries())
    .filter(([key]) => /question\[\w+\]/.test(key))
    .reduce<QuestionFilter>((acc, [key, value]) => {
      const questionId = key.replace('question[', '').replace(']', '');

      if (!acc[questionId]) {
        acc[questionId] = [value];
        return acc;
      }
      acc[questionId].push(value);
      return acc;
    }, {});

  return {
    search: searchParams.get('search') || undefined,
    ...(stageId
      ? {
          stage_id: stageId,
        }
      : {
          job_posting: jobPostingId,
        }),
    status: (searchParams.get('status') as RecruitmentProcessCollection['status']) || undefined,
    candidate__address_country__iin: searchParams.getAll('addressContryIn').join(',') || undefined,
    candidate__address_state__iin: searchParams.getAll('addressStateIn').join(',') || undefined,
    candidate__address_city__iin: searchParams.getAll('addressCityIn').join(',') || undefined,
    candidate__updated_at__date__gte: searchParams.get('updatedAtMin') || undefined,
    candidate__updated_at__date__lte: searchParams.get('updatedAtMax') || undefined,
    created_at__date__gte: searchParams.get('createdAtMin') || undefined,
    created_at__date__lte: searchParams.get('createdAtMax') || undefined,
    function_id: functionIdQueryParam,
    ...(functionIdQueryParam
      ? {
          match__score__gte: searchParams.get('match_min')
            ? Number(searchParams.get('match_min'))
            : undefined,
        }
      : {}),
    ...(functionIdQueryParam
      ? {
          match__score__lte: searchParams.get('match_max')
            ? Number(searchParams.get('match_max'))
            : undefined,
        }
      : {}),
    ...(functionId && searchParams.get('testCompleteness') === 'MISSING'
      ? { match__score__isnull: true }
      : {}),
    ...(functionId && searchParams.get('testCompleteness') === 'FINALIZED'
      ? { match__score__isnull: false }
      : {}),
    logic__gte: searchParams.get('logic_min') ? Number(searchParams.get('logic_min')) : undefined,
    logic__lte: searchParams.get('logic_max') ? Number(searchParams.get('logic_max')) : undefined,
    motivational__gte: searchParams.get('motivational_min')
      ? Number(searchParams.get('motivational_min'))
      : undefined,
    motivational__lte: searchParams.get('motivational_max')
      ? Number(searchParams.get('motivational_max'))
      : undefined,
    ocp__gte: searchParams.get('ocp_min') ? Number(searchParams.get('ocp_min')) : undefined,
    ocp__lte: searchParams.get('ocp_max') ? Number(searchParams.get('ocp_max')) : undefined,
    raw_potential__gte: searchParams.get('raw_potential_min')
      ? Number(searchParams.get('raw_potential_min'))
      : undefined,
    raw_potential__lte: searchParams.get('raw_potential_max')
      ? Number(searchParams.get('raw_potential_max'))
      : undefined,
    sjt__gte: searchParams.get('sjt_min') ? Number(searchParams.get('sjt_min')) : undefined,
    sjt__lte: searchParams.get('sjt_max') ? Number(searchParams.get('sjt_max')) : undefined,
    operational:
      searchParams.get('operational_logic') === 'Aprovado'
        ? 1
        : searchParams.get('operational_logic') === 'Reprovado'
        ? 0
        : undefined,
    candidate__birthdate__lte: birthdateEnd,
    candidate__birthdate__gte: birthdateStart,
    candidate__salary_expectation__lte: searchParams.get('salary_expectation_end')
      ? parseInt(searchParams.get('salary_expectation_end') as string, 10)
      : undefined,
    candidate__salary_expectation__gte: searchParams.get('salary_expectation_start')
      ? parseInt(searchParams.get('salary_expectation_start') as string, 10)
      : undefined,
    candidate__disabilities__iin: searchParams.getAll('disabilities').join(',') || undefined,
    has_answered_questions:
      searchParams.get('additionalQuestionsCompleteness') === 'FINALIZED'
        ? true
        : searchParams.get('additionalQuestionsCompleteness') === 'MISSING'
        ? false
        : undefined,
    question_filters:
      Object.keys(questionFilters).length > 0
        ? JSON.stringify(
            Object.entries(questionFilters)
              .filter(([_, values]) => values[0] !== '')
              .map(([questionId, values]) => ({
                question_id: parseInt(questionId, 10),
                value: values,
              }))
          )
        : undefined,
    candidate_source_option__in:
      searchParams.getAll('candidateSourceOptions').join(',') || undefined,
    candidate__tags__id__in: searchParams.getAll('tags').join(',') || undefined,
  };
};

//#endregion

//#region Permission building

const buildPermissionsMap = (permissions: {
  results: PermissionCollectionSerializer[];
}): PermissionCodenames => {
  const codenameIds: Record<string, number> = {};

  for (const perm of permissions.results) {
    codenameIds[perm.codename] = perm.id;
  }

  // Type-check that all required keys exist
  return codenameIds as unknown as PermissionCodenames;
};

//#endregion
//#region Vixting Query Params utils

const getVixtingQueryParams = (prefix: 'vixtingjobposting' | 'vixtingrequestjobposting') =>
  ({
    COMPANY: `${prefix}__company`,
    JOB_ROLE: `${prefix}__job_role`,
    DEPARTMENT: `${prefix}__department`,
    AFFILIATE: `${prefix}__affiliate`,
    UNIT_BUSINESS: `${prefix}__unit_business`,
  } as const);

const VixtingParamsUtils = {
  // Lista todos os parâmetros como array
  getParamsList: (queryParams: ReturnType<typeof getVixtingQueryParams>) =>
    Object.values(queryParams),

  // Verifica se existe algum parâmetro Vixting nos searchParams
  hasAnyParams: (
    searchParams: URLSearchParams,
    queryParams: ReturnType<typeof getVixtingQueryParams>
  ): boolean => {
    return VixtingParamsUtils.getParamsList(queryParams).some(
      (param) => searchParams.has(param) && searchParams.get(param) !== ''
    );
  },

  // Extrai todos os valores dos parâmetros Vixting
  extractParams: (
    searchParams: URLSearchParams,
    queryParams: ReturnType<typeof getVixtingQueryParams>
  ) => ({
    company: searchParams.get(queryParams.COMPANY) || undefined,
    jobRole: searchParams.get(queryParams.JOB_ROLE) || undefined,
    department: searchParams.get(queryParams.DEPARTMENT) || undefined,
    affiliate: searchParams.get(queryParams.AFFILIATE) || undefined,
    unitBusiness: searchParams.get(queryParams.UNIT_BUSINESS) || undefined,
  }),

  // Limpa todos os parâmetros Vixting
  clearParams: (
    searchParams: URLSearchParams,
    queryParams: ReturnType<typeof getVixtingQueryParams>
  ): void => {
    VixtingParamsUtils.getParamsList(queryParams).forEach((param) => {
      searchParams.delete(param);
    });
  },

  // Cria objeto para API com undefined para valores vazios
  getApiParams: (
    searchParams: URLSearchParams,
    queryParams: ReturnType<typeof getVixtingQueryParams>
  ) => {
    const params = VixtingParamsUtils.extractParams(searchParams, queryParams);
    return {
      [queryParams.COMPANY]: params.company,
      [queryParams.AFFILIATE]: params.affiliate,
      [queryParams.JOB_ROLE]: params.jobRole,
      [queryParams.DEPARTMENT]: params.department,
      [queryParams.UNIT_BUSINESS]: params.unitBusiness,
    };
  },
};

const useVixtingParams = (
  searchParams: URLSearchParams,
  prefix: 'vixtingjobposting' | 'vixtingrequestjobposting' = 'vixtingjobposting'
) => {
  const queryParams = getVixtingQueryParams(prefix);
  const params = VixtingParamsUtils.extractParams(searchParams, queryParams);
  const apiParams = VixtingParamsUtils.getApiParams(searchParams, queryParams);
  const hasParams = VixtingParamsUtils.hasAnyParams(searchParams, queryParams);

  return {
    params,
    apiParams,
    hasParams,
    queryParams,
    clear: () => VixtingParamsUtils.clearParams(searchParams, queryParams),
  };
};

//#endregion

export {
  calculateOpenDays,
  markdownToQuillHtml,
  cleanQuillHtmlContent,
  currencyFormatter,
  currencyParser,
  //#region Vixting Mappings
  mapVixtingCompany,
  mapVixtingJobRole,
  mapVixtingDepartment,
  //#endregion
  //#region Query param building
  buildCandidateQueryParam,
  buildRecruitmentProcessQueryParam,
  //#endregion
  //#region Permission building
  buildPermissionsMap,
  //#endregion
  //#region Vixting Query Params utils
  getVixtingQueryParams,
  VixtingParamsUtils,
  useVixtingParams,
  //#endregion
};
